/*
Attributes:
  checked: the checked state, and the default a reset goes back to
  value: the string submitted with a form when ticked (read it as `formValue`;
         the `value` property is the checked boolean, like every other input)
  help-tooltip-default-text: Default text for help tooltip
  help-tooltip-dict-key: Localization key for help tooltip
  help-placement: Position of help tooltip (default: 'right')
*/

import './as-help-hover.ts';
import { updateInputHelpHover } from './input-help-hover-utils.ts';
import type { InputChangeReason, SetValueOptions } from './types';
import { resolveSetValueSilence, upgradeProperty } from './utils';
import { lookupParam } from '../../store/paramStore.ts';
import { AsInputBase, type InternalSetValueOptions } from './as-input-base.ts';
import { applyLocalizedText } from '../base.ts';

export class AsCheckbox extends AsInputBase<'bool'> {
  static readonly tag = 'as-checkbox' as const;

  static get observedAttributes(): string[] {
    return ['value', 'checked', 'label-default-text', 'label-dict-key', 'disabled', 'help-tooltip-default-text', 'help-tooltip-dict-key', 'help-placement'];
  }

  private _inputEl: HTMLInputElement | null = null;
  private _labelTextEl: HTMLElement | null = null;
  private _helpHoverEl: HTMLElement | null = null;
  private _formValue = '';
  private _checked = false;
  private _lastCommittedChecked = false;
  private _isReflectingCheckedAttr = false;
  private _hasRendered = false;

  constructor() {
    super();
    this._handleInput = this._handleInput.bind(this);
    this._handleChange = this._handleChange.bind(this);
  }

  // ============================================================================
  // Abstract method implementations (required by AsInputBase)
  // ============================================================================

  protected _getValue(): boolean {
    return this._checked;
  }

  protected _applyValue(val: boolean, options: InternalSetValueOptions): void {
    this._setCheckedInternal(val, options);
  }

  protected _convertFromParamate(val: any): boolean {
    // Attribute values arrive as strings, where a plain Boolean() would read
    // "false" and "0" as true. Paramate itself sends real booleans or 0/1.
    if (typeof val === 'string') {
      const s = val.trim().toLowerCase();
      return !(s === '' || s === 'false' || s === '0');
    }
    return Boolean(val);
  }

  /** A checkbox declares its default with the `checked` attribute, not `value`
   *  (which is the form value string). No attribute means default-unchecked,
   *  same as plain HTML — so this never falls through to a snapshot. */
  protected override _authoredDefault(): boolean {
    return this.hasAttribute('checked');
  }

  protected _getInputElement(): HTMLInputElement | null {
    return this._inputEl;
  }

  protected override _applyReadonlyState(): void {
    // Checkboxes have no native readOnly; handled via event guards
  }

  // ============================================================================
  // Lifecycle
  // ============================================================================

  connectedCallback(): void {
    super.connectedCallback();
    if (!this._hasRendered) {
      this._render();
      this._hasRendered = true;
    }

    upgradeProperty(this, 'checked');
    upgradeProperty(this, 'value');

    if (this.hasAttribute('value')) {
      this._formValue = this.getAttribute('value') ?? '';
    }

    if (this.hasAttribute('checked')) {
      this._setCheckedInternal(true, {
        emitInput: false,
        emitChange: false,
        reflectAttr: false,
        commit: true,
        silent: true,
      });
    } else {
      this._setCheckedInternal(false, {
        emitInput: false,
        emitChange: false,
        reflectAttr: false,
        commit: true,
        silent: true,
      });
    }

    // Register with Paramate (from base class)
    this._registerParamate();

    this._applyDisabledState();
    this._updateLabel();

    this._inputEl?.addEventListener('input', this._handleInput);
    this._inputEl?.addEventListener('change', this._handleChange);
  }

  disconnectedCallback(): void {
    this._inputEl?.removeEventListener('input', this._handleInput);
    this._inputEl?.removeEventListener('change', this._handleChange);

    // Unregister from Paramate (from base class)
    this._unregisterParamate();
    super.disconnectedCallback();
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) {
      return;
    }

    switch (name) {
      case 'value':
        this._formValue = newValue ?? '';
        if (this._inputEl) {
          this._inputEl.value = this._formValue;
        }
        break;
      case 'checked':
        if (this._isReflectingCheckedAttr) {
          return;
        }
        this._setCheckedInternal(newValue !== null, {
          emitInput: false,
          emitChange: false,
          reflectAttr: false,
          commit: true,
          reason: 'api',
        });
        break;
      case 'label-default-text':
      case 'label-dict-key':
        this._updateLabel();
        break;
      case 'disabled':
        this._applyDisabledState();
        break;
      case 'help-tooltip-default-text':
      case 'help-tooltip-dict-key':
      case 'help-placement':
        this._updateHelpHover();
        break;
    }
  }

  // ============================================================================
  // Public API
  // ============================================================================

  /** The checked state — same meaning as `value` on every other input, and the
   *  same thing appData and Paramate store. The HTML form value lives on
   *  `formValue`, which is what the `value` attribute still backs. */
  get value(): boolean {
    return this._checked;
  }

  set value(val: boolean) {
    this._warnOnValueSetter();
    this.setValue(Boolean(val), { silenceChangeEv: true });
  }

  /** The string submitted with a form when this box is ticked. Backed by the
   *  `value` attribute; unrelated to the checked state. */
  get formValue(): string {
    return this._formValue;
  }

  set formValue(val: string) {
    this._formValue = val ?? '';
    if (this._inputEl) {
      this._inputEl.value = this._formValue;
    }
    this.setAttribute('value', this._formValue);
  }

  get checked(): boolean {
    return this._checked;
  }

  set checked(val: boolean) {
    this.setValue(Boolean(val), { silenceChangeEv: true });
  }

  public setValue(val: boolean, opt?: SetValueOptions): void {
    const { silenceInput, silenceChange, silenceHistory, reason } = resolveSetValueSilence(opt);
    if (silenceHistory) this._skipNextHistoryPush = true;
    this._setCheckedInternal(Boolean(val), {
      emitInput: !silenceInput,
      emitChange: !silenceChange,
      reflectAttr: true,
      commit: true,
      reason,
    });
    this._skipNextHistoryPush = false;
  }

  // ============================================================================
  // Private methods
  // ============================================================================

  private _render(): void {
    // CRITICAL: Save hovercard/custom label BEFORE wiping innerHTML (which destroys all children).
    // A `[slot="label"]` child (e.g. containing an <as-text> + <a> link) overrides the plain-text
    // label-default-text/label-dict-key attributes -- lets callers embed a real link in the label
    // while every existing attribute-only usage keeps rendering exactly as before.
    const savedHovercard = this.querySelector('as-hovercard');
    const savedLabel = this.querySelector(':scope > [slot="label"]');
    // No label attributes means no label. This used to fall back to the
    // placeholder text "Boolean", which was only ever right by accident and
    // had to be styled away in compact layouts (a checkbox in a table row).
    const hasLabelText = this.hasAttribute('label-dict-key') || this.hasAttribute('label-default-text');

    this.innerHTML = /* html */`
      <label class="h-flex" style="display:flex; flex-direction:row; width:100%; align-items:center; gap: 0.5rem; min-height: 1.5rem;">
        <div class="label-container" style="display:flex; align-items:center; gap:4px; flex-shrink: 1; min-width: 0;">
          ${savedLabel || !hasLabelText ? '' : `<as-text class="label-text" dict-key="${this.getAttribute('label-dict-key') ?? ''}">${this.getAttribute('label-default-text') ?? ''}</as-text>`}
          <span class="help-hover-container"></span>
        </div>
        <div class="spacer" style="flex:1; height:1px; background-color: gainsboro; align-self: center;"></div>
        <input type="checkbox" style="flex-shrink: 0;" />
      </label>
    `;
    this._inputEl = this.querySelector('input');
    this._labelTextEl = this.querySelector('.label-text');
    this._helpHoverEl = this.querySelector('.help-hover-container');

    if (savedLabel) {
      this.querySelector('.label-container')?.prepend(savedLabel);
    }

    if (this._inputEl) {
      this._inputEl.value = this._formValue;
      this._inputEl.checked = this._checked;
    }

    // Pass saved hovercard since it was destroyed by innerHTML
    this._updateHelpHover(savedHovercard);
  }

  private _handleInput(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    if (!target) return;
    if (this.readonly) {
      target.checked = this._checked;
      return;
    }
    this._setCheckedInternal(target.checked, {
      emitInput: true,
      emitChange: false,
      reflectAttr: true,
      commit: false,
      reason: 'user',
    });
  }

  private _handleChange(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    if (!target) return;
    if (this.readonly) {
      target.checked = this._checked;
      return;
    }
    this._setCheckedInternal(target.checked, {
      emitInput: false,
      emitChange: true,
      reflectAttr: true,
      commit: true,
      reason: 'user',
    });
  }

  private _setCheckedInternal(checked: boolean, options: InternalSetValueOptions): void {
    const {
      emitInput = false,
      emitChange = false,
      reflectAttr = false,
      commit = false,
      silent = false,
      reason = 'api',
    } = options;

    const previous = this._checked;
    const previousCommitted = this._lastCommittedChecked;
    this._checked = checked;
    this._value = checked; // Sync base class state

    if (this._inputEl) {
      this._inputEl.checked = checked;
    }

    if (reflectAttr) {
      this._isReflectingCheckedAttr = true;
      try {
        if (checked) {
          this.setAttribute('checked', '');
        } else {
          this.removeAttribute('checked');
        }
      } finally {
        this._isReflectingCheckedAttr = false;
      }
    }

    const shouldEmitInput = emitInput && previous !== checked;
    // A committed change always reports itself, loudly or on the sync channel.
    const committedChange = previous !== checked || previousCommitted !== checked;

    if (commit || (emitChange && committedChange)) {
      this._lastCommittedChecked = checked;
    }

    if (shouldEmitInput) {
      this._emitInput(checked, previous, reason);
    }

    if (!silent && (emitChange || commit) && committedChange) {
      this._emitChange(checked, previousCommitted, this._commitPhase(emitChange), reason);
    }
  }

  private _detail(checked: boolean, previous: boolean) {
    return {
      type: 'checkbox' as const,
      value: checked,
      checked,
      previousValue: previous,
      previousChecked: previous,
      formValue: this._formValue,
    };
  }

  private _emitInput(checked: boolean, previous: boolean, reason: InputChangeReason): void {
    this._emitValueEvent('input', reason, this._detail(checked, previous));
  }

  private _emitChange(
    checked: boolean,
    previous: boolean,
    phase: 'change' | 'sync',
    reason: InputChangeReason,
  ): void {
    this._emitValueEvent(phase, reason, this._detail(checked, previous));

    // Ground truth coming back from the configurator must not be sent straight
    // back to it, and a sync-phase change isn't a user edit to push either.
    if (phase !== 'change' || reason === 'paramate') return;

    // Int params require 0/1 — booleans are rejected by the configurator
    const paramType = lookupParam(this._configurator?.params, this._paramName)?.type;
    this._sendToParamate(paramType === 'int' ? (checked ? 1 : 0) : checked);
  }

  protected override _onLabelVarsChanged(): void {
    this._updateLabel();
  }

  private _updateLabel(): void {
    if (!this._labelTextEl) return;
    const dictKey = this.getAttribute('label-dict-key');
    const defaultText = this.getAttribute('label-default-text') ?? 'Boolean';
    this._applyLabelVars(this._labelTextEl);
    applyLocalizedText(this._labelTextEl, dictKey, defaultText);
  }

  private _updateHelpHover(savedHovercard?: Element | null): void {
    updateInputHelpHover(this, this._helpHoverEl, savedHovercard);
  }
}

AsCheckbox.define();
